"use client"

// components/tracker/drug-identity-context.tsx
//
// THE identity rule for a drug's face and colour (FE-DRUG-AVATAR-1, Phase 3a).
//
// A tracker route mounts `DrugIdentityProvider` with the `assetIdByDrug` map
// its own payload carries (the landscape surface, the Trials page, the
// Changes page). Below it, a component asks for a drug by DISPLAY NAME and
// gets back the canonical asset ID, the sphere's data URI, or the sphere's
// colour. The seed is the asset ID whenever one resolves, and the normalised
// name only as the explicit fallback; it is never list position, sponsor or
// the watch's ordering (see `lib/drug-avatars/avatar-seed.ts`).
//
// The map is keyed the way `drugCounts` is keyed: by display name. Sites
// sometimes hold a trimmed or differently cased copy of that name, so the
// lookup also tries a case-folded match. Forgiving on the LOOKUP, strict on
// the SEED: a case-folded hit still seeds by the asset ID it finds.
//
// Outside any provider the hooks fall back to the name seed and, in
// development, warn once per process. That is the landing demo, the sponsor
// news preview and the fixture-named tiles, all of which have no ids by
// design; a production route that trips it is a missing mount.

import { createContext, useContext, useMemo, type ReactNode } from "react"

import { drugColorFromAccent, type DrugColor } from "@/components/tracker/drug-colors"
import { accentForDrug, avatarUriForDrug } from "@/lib/drug-avatars/avatar-cache"

export type AssetIdByDrug = Record<string, string | null>

type DrugIdentityValue = {
  exact: AssetIdByDrug
  /** Case-folded, whitespace-collapsed display name -> asset id. */
  folded: Map<string, string | null>
}

const DrugIdentityContext = createContext<DrugIdentityValue | null>(null)

function fold(name: string): string {
  return name.normalize("NFC").trim().toLowerCase().replace(/\s+/g, " ")
}

export function DrugIdentityProvider({
  assetIdByDrug,
  children,
}: {
  assetIdByDrug: AssetIdByDrug | null | undefined
  children: ReactNode
}) {
  const value = useMemo<DrugIdentityValue>(() => {
    const exact = assetIdByDrug ?? {}
    const folded = new Map<string, string | null>()
    for (const [name, id] of Object.entries(exact)) {
      const key = fold(name)
      // Two display names folding together with different ids is a
      // contradiction, not a hit: the first entry wins the folded slot only
      // when nothing else claimed it, and a later different id blanks it.
      if (!folded.has(key)) folded.set(key, id)
      else if (folded.get(key) !== id) folded.set(key, null)
    }
    return { exact, folded }
  }, [assetIdByDrug])

  return (
    <DrugIdentityContext.Provider value={value}>
      {children}
    </DrugIdentityContext.Provider>
  )
}

let warnedOutsideProvider = false

function useDrugIdentityValue(): DrugIdentityValue | null {
  const value = useContext(DrugIdentityContext)
  if (value === null && process.env.NODE_ENV === "development" && !warnedOutsideProvider) {
    warnedOutsideProvider = true
    console.warn(
      "[drug-identity] a drug identity hook ran outside DrugIdentityProvider; " +
        "seeding by name. Mount the provider from the route's payload.",
    )
  }
  return value
}

/**
 * THE lookup: exact key first, case-folded second, with the contradiction
 * guard on both. One function, shared by `useDrugAssetId` and the resolver
 * `useDrugIdentityResolver` hands to non-component helpers, so the two
 * cannot drift (3b hard stop).
 */
function resolveAssetId(
  value: DrugIdentityValue | null,
  name: string | null | undefined,
): string | null {
  if (!value || !name) return null
  const foldedId = value.folded.get(fold(name)) ?? null
  if (Object.prototype.hasOwnProperty.call(value.exact, name)) {
    // The contradiction guard applies to exact hits too: when another display
    // name folding to the same key carries a different id, the folded slot is
    // blank and this name's identity is ambiguous by casing alone, so the
    // exact spelling must not win what the folded one loses (3a review, #6).
    return foldedId === null ? null : (value.exact[name] ?? null)
  }
  return foldedId
}

/**
 * The canonical asset ID for a display name, or null when the payload did not
 * resolve one (or no provider is mounted).
 */
export function useDrugAssetId(name: string | null | undefined): string | null {
  const value = useDrugIdentityValue()
  return resolveAssetId(value, name)
}

/**
 * The same lookup as `useDrugAssetId`, as a memoised function, for the places
 * that build per-drug data outside a component (the readouts calendar helper
 * writes one avatar and one accent into every event, and no component exists
 * per event to run a hook in). Stable while the provider's map is.
 */
export function useDrugIdentityResolver(): (name: string) => string | null {
  const value = useDrugIdentityValue()
  return useMemo(() => (name: string) => resolveAssetId(value, name), [value])
}

/** Which seed painted a sphere: the canonical asset ID, or the name fallback. */
export type DrugAvatarIdentity = "asset" | "name"

export type DrugAvatarFace = { src: string; identity: DrugAvatarIdentity }
type DrugAvatarFaceOrNone = DrugAvatarFace | { src: null; identity: null }

/**
 * The sphere's data URI for a display name AND which seed drew it, so every
 * element that paints a sphere can carry `data-identity="asset" | "name"`
 * and the name fallback stays visible in the DOM (Gate 2 marker; 3b review,
 * #4). Null in, null out: a row whose drug is unattributed keeps its neutral
 * slot, and the hook still runs unconditionally at the component's top level.
 */
export function useDrugAvatar(name: string): DrugAvatarFace
export function useDrugAvatar(name: string | null | undefined): DrugAvatarFaceOrNone
export function useDrugAvatar(name: string | null | undefined): DrugAvatarFaceOrNone {
  const assetId = useDrugAssetId(name)
  return useMemo<DrugAvatarFaceOrNone>(() => {
    if (!name) return { src: null, identity: null }
    return {
      src: avatarUriForDrug(assetId, name),
      identity: assetId ? "asset" : "name",
    }
  }, [assetId, name])
}

/**
 * The sphere's data URI alone, for the sites that hand it to an image prop
 * rather than paint a span. A thin wrapper over `useDrugAvatar`.
 */
export function useDrugAvatarUri(name: string): string
export function useDrugAvatarUri(name: string | null | undefined): string | null
export function useDrugAvatarUri(name: string | null | undefined): string | null {
  return useDrugAvatar(name).src
}

/**
 * The sphere's colour for a display name, in the chip/monogram shape the
 * colour call sites read. Same seed as `useDrugAvatarUri`, so the chip is
 * always the sphere's own hue. Null for an empty name.
 */
export function useDrugColor(name: string | null | undefined): DrugColor | null {
  const assetId = useDrugAssetId(name)
  if (!name || !name.trim()) return null
  return drugColorFromAccent(accentForDrug(assetId, name))
}

/**
 * A page's asset map over the surface's, taking a page value only when it is
 * a resolved id. The resolver's degrade paths write every name as null, and
 * a null must not override a good surface id, or the home would show an
 * asset sphere and the page a name sphere in one session (3b review, #1). A
 * name the surface has never seen still lands as null so the lookup stays
 * honest about it.
 */
export function mergeAssetIdMaps(
  surface: AssetIdByDrug | null | undefined,
  page: AssetIdByDrug | null | undefined,
): AssetIdByDrug {
  const out: AssetIdByDrug = { ...(surface ?? {}) }
  for (const [name, id] of Object.entries(page ?? {})) {
    if (typeof id === "string" && id.length > 0) out[name] = id
    else if (!(name in out)) out[name] = null
  }
  return out
}

export const _internals = {
  fold,
  resetWarning: () => {
    warnedOutsideProvider = false
  },
}
